import { apiService } from './ApiService';
import {
  GetInspectionPlansResponse,
  GetLineItemsResponse,
  LineItem,
  ScheduledInspection
} from '@/types/response/InspectionPlanningResponses';
import { 
  CopyPasteInspectionPlanRequest,
  CreateGroupRequest,
  UpdateGroupRequest,
  CreateLineItemRequest,
  UpdateLineItemRequest,
  UpdateInspectionPlanRequest,
  CheckLineItemExistsRequest
} from '@/types/request/InspectionPlanningRequests';

export interface PlanningGroup {
  id: number;
  groupIdentifier: string;
  title: string;
  lineItems?: PlanningLineItem[] | null;
}

export interface PlanningLineItem {
  id: number;
  lineItem: string;
  title: string;
  majorRequirement: string;
  relatedInfo: string | null;
  estimatedQuantity: string | null;
  performanceStandard: string | null;
  lineItemId: number;
  lastUpdated: string;
  attachmentCount: number;
  attachments?: Array<{
    Id: number;
    Description: string;
    Link: string | null;
    FileId?: number | null;
    Filename?: string | null;
  }>;
  modificationStatus: string;
  isActive: boolean;
  inspectionPlan?: InspectionPlanItem | null;
}

export interface InspectionPlanItem {
  id: number;
  inspectionMethodId?: number | null;
  expertiseAreaId?: number | null;
  primaryValue?: string | null;
  alternateValue?: string | null;
  timeToInspect?: string | null;
  sampleSize?: string | null;
  sampleSelection?: string | null;
  additionalInstructions?: string | null;
  isActive: boolean;
  isLocked: boolean;
  status: string;
  autoInspections?: any;
  fixedInspections?: any[];
  checklistItems?: any[];
  scheduledInspectionsCount?: number;
  scheduledInspections?: ScheduledInspection[];
  inspectionPlanLastUpdate?: string;
  attachments?: Array<{
    Id: number;
    Description: string;
    Link: string | null;
    FileId?: number | null;
    Filename?: string | null;
  }>;
}

export interface PlanningFilters {
  showAll: boolean;
  showActiveLineItems: boolean;
  showInactiveLineItems: boolean;
  showActiveInspectionPlans: boolean;
  showInactiveInspectionPlans: boolean;
  showLockedInspectionPlans: boolean;
  showUnlockedInspectionPlans: boolean;
}

/** Flattened, one-row-per-group shape used by the Planning desktop grid view. */
export interface PlanningGroupGridRow {
  id: number;
  groupIdentifier: string;
  title: string;
  lineItemCount: number;
}

/** Flattened, one-row-per-line-item shape used by the Planning desktop grid view. */
export interface PlanningLineItemGridRow {
  id: number;
  groupId: number;
  groupIdentifier: string;
  groupTitle: string;
  lineItem: string;
  title: string;
  majorRequirement: string;
  isActive: boolean;
  modificationStatus: string;
  lastUpdated: string;
  attachmentCount: number;
}

/** Flattened, one-row-per-inspection-plan shape used by the Planning desktop grid view. */
export interface PlanningInspectionPlanGridRow {
  id: number;
  lineItemId: number;
  groupId: number;
  groupIdentifier: string;
  groupTitle: string;
  lineItem: string;
  lineItemTitle: string;
  isActive: boolean;
  isLocked: boolean;
  status: string;
  primaryValue: string;
  alternateValue: string;
  timeToInspect: string;
  sampleSize: string;
  scheduledInspectionsCount: number;
  checklistItemCount: number;
}

export type BulkAction = 
  | 'activate-line-items'
  | 'deactivate-line-items'
  | 'activate-inspection-plans'
  | 'deactivate-inspection-plans'
  | 'lock-inspection-plans'
  | 'unlock-inspection-plans';

export interface BulkActionSelection {
  selectedGroups: Set<number>;
  selectedLineItems: Set<number>;
}

export interface BulkLineItemActionResult {
  successIds: number[];
  failed: Array<{ id: number; message: string }>;
}

class PlanningService {
  
  public async getGroupedPlanningData(): Promise<PlanningGroup[]> {
    try {
      const [contractLineItemGroupsResponse, inspectionPlanGroupsResponse] = await Promise.all([
        apiService.getContractLineItems() as Promise<GetLineItemsResponse>,
        apiService.getInspectionPlans() as Promise<GetInspectionPlansResponse>,
      ]);
      const groups = this.buildPlanningGroups(
        contractLineItemGroupsResponse,
        inspectionPlanGroupsResponse,
      );

      //compare and sort groups by groupIdentifier as numbers, not strings, to ensure correct order (e.g., 2 comes before 10)
      return groups.sort((a, b) => Number(a.groupIdentifier) - Number(b.groupIdentifier));
    } catch (error) {
      console.error('Error fetching planning data:', error);
      throw new Error('Failed to load planning data');
    }
  }

  public async getLineItemsForGroup(groupId: number): Promise<PlanningLineItem[]> {
    try {
      const [contractLineItemGroupsResponse, inspectionPlanGroupsResponse] = await Promise.all([
        apiService.getContractLineItems() as Promise<GetLineItemsResponse>,
        apiService.getInspectionPlans() as Promise<GetInspectionPlansResponse>,
      ]);
      const groups = this.buildPlanningGroups(
        contractLineItemGroupsResponse,
        inspectionPlanGroupsResponse,
      );
      const targetGroup = groups.find(group => group.id === groupId);
      if (!targetGroup) {
        throw new Error(`Group with ID ${groupId} not found`);
      }
      const lineItems = targetGroup.lineItems ?? [];
      return lineItems.sort((a, b) => String(a.lineItem).localeCompare(String(b.lineItem)));
    } catch (error) {
      console.error(`Error fetching line items for group ${groupId}:`, error);
      throw new Error('Failed to load line items for group');
    }
  }

  public async getInspectionPlanForLineItem(lineItemId: number): Promise<InspectionPlanItem | null> {
    try {
      // Get inspection plans data
      const inspectionPlansResponse = await apiService.getInspectionPlans();
      
      // Find the line item across all groups
      for (const group of inspectionPlansResponse) {
        const lineItem = group.LineItems.find(item => item.Id === lineItemId);
        if (lineItem) {
          return this.transformLineItemToInspectionPlan(lineItem);
        }
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching inspection plan for line item ${lineItemId}:`, error);
      throw new Error('Failed to load inspection plan for line item');
    }
  }

  public filterPlanningGroups(
    groups: PlanningGroup[], 
    filters: PlanningFilters
  ): PlanningGroup[] {
    if (filters.showAll) {
      return groups;
    }

    return groups.map(group => ({
      ...group,
      lineItems: group.lineItems?.filter(lineItem => {
        if (!filters.showInactiveLineItems && !lineItem.isActive) {
          return false;
        }

        if (!filters.showActiveLineItems && lineItem.isActive) {
          return false;
        }

        if (!filters.showInactiveInspectionPlans && !lineItem.inspectionPlan?.isActive) {
          return false;
        }

        if (!filters.showActiveInspectionPlans && lineItem.inspectionPlan?.isActive) {
          return false;
        }

        if (!filters.showLockedInspectionPlans && (lineItem.inspectionPlan?.isLocked ?? true)) {
          return false;
        }

        if (!filters.showUnlockedInspectionPlans && !(lineItem.inspectionPlan?.isLocked ?? false)) {
          return false;
        }

        return true;
      }) ?? null
    })).filter(group => (group.lineItems?.length ?? 0) > 0);
  }

  public async createGroup(data: CreateGroupRequest): Promise<number> {
    try {
      return await apiService.createGroup(data);
    } catch (error) {
      console.error('Error creating group:', error);
      throw new Error('Failed to create group');
    }
  }

  public async updateGroup(data: UpdateGroupRequest): Promise<number> {
    try {
      return await apiService.updateGroup(data);
    } catch (error) {
      console.error('Error updating group:', error);
      throw new Error('Failed to update group');
    }
  }

  public async createLineItem(data: CreateLineItemRequest): Promise<number> {
    try {
      return await apiService.createLineItem(data);
    } catch (error) {
      console.error('Error creating line item:', error);
      throw new Error('Failed to create line item');
    }
  }

  public async updateLineItem(data: UpdateLineItemRequest): Promise<number> {
    try {
      return await apiService.updateLineItem(data);
    } catch (error) {
      console.error('Error updating line item:', error);
      throw new Error('Failed to update line item');
    }
  }

  public async checkLineItemExists(data: CheckLineItemExistsRequest): Promise<boolean> {
    try {
      return await apiService.checkLineItemExists(data);
    } catch (error) {
      console.error('Error checking line item uniqueness:', error);
      throw new Error('Failed to validate line item identifier');
    }
  }

  public async updateInspectionPlan(data: UpdateInspectionPlanRequest): Promise<number> {
    try {
      return await apiService.updateInspectionPlan(data);
    } catch (error) {
      console.error('Error updating inspection plan:', error);
      throw new Error('Failed to update inspection plan');
    }
  }

  public async copyPasteInspectionPlan(data: CopyPasteInspectionPlanRequest): Promise<number> {
    try {
      return await apiService.copyPasteInspectionPlan(data);
    } catch (error) {
      console.error('Error copying inspection plan:', error);
      throw new Error('Failed to copy inspection plan');
    }
  }

  public async resetInspectionPlan(inspectionPlanIds: number[]): Promise<number> {
    try {
      return await apiService.resetInspectionPlan(inspectionPlanIds);
    } catch (error) {
      console.error('Error resetting inspection plan:', error);
      throw new Error('Failed to reset inspection plan');
    }
  }

  public async activateDeactivateInspectionPlan(activeIds: number[], inactiveIds: number[]): Promise<number> {
    try {
      return await apiService.activateDeactivateInspectionPlan(activeIds, inactiveIds);
    } catch (error) {
      console.error('Error activating/deactivating inspection plan:', error);
      throw new Error('Failed to activate/deactivate inspection plan');
    }
  }

  public async lockUnlockInspectionPlan(lockIds: number[], unlockIds: number[], reason: string): Promise<number> {
    try {
      return await apiService.lockUnlockInspectionPlan(lockIds, unlockIds, reason);
    } catch (error) {
      console.error('Error locking/unlocking inspection plan:', error);
      throw new Error('Failed to lock/unlock inspection plan');
    }
  }

  public async activateLineItems(lineItemIds: number[]): Promise<BulkLineItemActionResult> {
    return this.executeLineItemBulkAction(lineItemIds, async (lineItemId: number) => {
      await apiService.activateLineItem(lineItemId);
    });
  }

  public async inactivateLineItems(lineItemIds: number[], reason: string): Promise<BulkLineItemActionResult> {
    return this.executeLineItemBulkAction(lineItemIds, async (lineItemId: number) => {
      await apiService.inactivateLineItem(lineItemId, reason);
    });
  }

  public getSelectionWithHierarchy(
    selection: BulkActionSelection,
    groups: PlanningGroup[]
  ): BulkActionSelection {
    const result: BulkActionSelection = {
      selectedGroups: new Set(selection.selectedGroups),
      selectedLineItems: new Set(selection.selectedLineItems),
    };

    // If a group is selected, select all its line items
    for (const groupId of selection.selectedGroups) {
      const group = groups.find(g => g.id === groupId);
      if (group?.lineItems) {
        for (const lineItem of group.lineItems) {
          result.selectedLineItems.add(lineItem.id);
        }
      }
    }

    return result;
  }

  /**
   * Resolve the inspection plan IDs for the given selected line items.
   * Since there is one inspection plan per line item, this maps from line item IDs.
   */
  public getInspectionPlanIdsForSelectedLineItems(
    selectedLineItems: Set<number>,
    groups: PlanningGroup[]
  ): number[] {
    const ids: number[] = [];
    for (const lineItemId of selectedLineItems) {
      for (const group of groups) {
        const lineItem = group.lineItems?.find(li => li.id === lineItemId);
        if (lineItem?.inspectionPlan) {
          ids.push(lineItem.inspectionPlan.id);
          break;
        }
      }
    }
    return ids;
  }

  private async executeLineItemBulkAction(
    lineItemIds: number[],
    action: (lineItemId: number) => Promise<void>
  ): Promise<BulkLineItemActionResult> {
    const settledResults = await Promise.allSettled(
      lineItemIds.map(async (lineItemId: number) => {
        await action(lineItemId);
        return lineItemId;
      })
    );

    const successIds: number[] = [];
    const failed: Array<{ id: number; message: string }> = [];

    settledResults.forEach((result, index) => {
      const lineItemId = lineItemIds[index];
      if (result.status === 'fulfilled') {
        successIds.push(lineItemId);
      } else {
        failed.push({
          id: lineItemId,
          message: result.reason instanceof Error ? result.reason.message : 'Unknown error',
        });
      }
    });

    return { successIds, failed };
  }

  // Private transformation methods
  private buildPlanningGroups(
    contractLineItemGroups: GetLineItemsResponse,
    inspectionPlanGroups: GetInspectionPlansResponse,
  ): PlanningGroup[] {
    const inspectionPlanLineItemsById = new Map<number, LineItem>();

    for (const inspectionPlanGroup of inspectionPlanGroups) {
      for (const lineItem of inspectionPlanGroup.LineItems) {
        inspectionPlanLineItemsById.set(lineItem.Id, lineItem);
      }
    }

    return contractLineItemGroups.map(group => ({
        id: group.Id,
        groupIdentifier: group.Identifier,
        title: group.Title,
        lineItems: this.transformLineItemsToPlanningLineItems(
          group.LineItems,
          inspectionPlanLineItemsById,
        ),
      }));
  }

  private transformLineItemsToPlanningLineItems(
    lineItems: LineItem[],
    inspectionPlanLineItemsById?: Map<number, LineItem>,
  ): PlanningLineItem[] {
    return lineItems.map(item => ({
      id: item.Id,
      lineItem: item.LineItem,
      title: item.Title,
      majorRequirement: item.MajorRequirement,
      relatedInfo: item.RelatedInfo,
      estimatedQuantity: item.EstimatedQuantity,
      performanceStandard: item.PerformanceStandard,
      lineItemId: item.Id,
      lastUpdated: item.LastUpdate || item.LineItemLastUpdate || '',
      attachmentCount: item.AttachmentCount,
      attachments: item.Attachments ?? [],
      modificationStatus: item.ModificationStatus || '',
      isActive: item.IsActive ?? false,
      inspectionPlan: this.transformLineItemToInspectionPlan(inspectionPlanLineItemsById?.get(item.Id) ?? item)
    })).sort((a, b) => String(a.lineItem).localeCompare(String(b.lineItem)));
  }

  private transformLineItemToInspectionPlan(lineItem: LineItem): InspectionPlanItem | null {
    // Only return inspection plan if line item has inspection plan data
    if (lineItem.InspectionMethodId !== undefined || lineItem.ExpertiseAreaId !== undefined) {
      return {
        id: lineItem.Id,
        inspectionMethodId: lineItem.InspectionMethodId,
        expertiseAreaId: lineItem.ExpertiseAreaId,
        primaryValue: lineItem.PrimaryValue,
        alternateValue: lineItem.AlternateValue,
        timeToInspect: lineItem.TimeToInspect,
        sampleSize: lineItem.SampleSize,
        sampleSelection: lineItem.SampleSelection,
        additionalInstructions: lineItem.AdditionalInstructions,
        isActive: lineItem.IsActive ?? false,
        isLocked: lineItem.IsLocked ?? false,
        status: lineItem.Status ?? '',
        autoInspections: lineItem.AutoInspections,
        fixedInspections: lineItem.FixedInspections,
        checklistItems: lineItem.ChecklistItems,
        attachments: lineItem.Attachments ?? [],
        scheduledInspectionsCount: lineItem.ScheduledInspectionsCount,
        scheduledInspections: lineItem.ScheduledInspections ?? [],
        inspectionPlanLastUpdate: lineItem.InspectionPlanLastUpdate,
      };
    }
    
    return null;
  }
}

export const planningService = new PlanningService();
export default planningService;